import clsx from "clsx";
import { Outlet, Link, useLocation, type LinkProps } from "react-router";

import type { Assignment } from "@/types/assignment";

type RouteData = {
  url: string;
  title: string;
  index?: boolean;
  extra_match?: string;
  roles?: string[];
};

type AssignmentNavigationMenuProps = {
  assignment: Assignment;
  route_data?: RouteData[];
};

export default function AssignmentNavigationMenu({
  assignment,
  route_data = [],
}: AssignmentNavigationMenuProps) {
  if (route_data.length < 2) {
    return <Outlet context={[assignment]} />;
  }
  return (
    <>
      <div className="mx-auto w-full md:max-w-[80rem] md:px-2">
        <div className="mb-8">
          <TopNavigation assignment_name={assignment.name} routes={route_data} />
        </div>
      </div>
      <Outlet context={[assignment]} />
    </>
  );
}

const TopNavigation = ({
  assignment_name,
  routes,
}: {
  assignment_name: string;
  routes: RouteData[];
}) => {
  return (
    <div className="flex">
      <div className="mr-4 p-2 font-outfit font-medium text-slate-300">{assignment_name}</div>
      {routes.map((route) => {
        return (
          <NavLink
            key={route.url}
            to={route.url}
            index={route.index}
            extra_match={route.extra_match}
          >
            {route.title}
          </NavLink>
        );
      })}
    </div>
  );
};

// index and extra_match steer the match, they are kept off the anchor element
type NavLinkProps = Omit<LinkProps, "to" | "className"> & {
  to: string;
  index?: boolean | undefined;
  extra_match?: string | undefined;
};

const NavLink = ({ index, extra_match, to, children, ...rest }: NavLinkProps) => {
  const location = useLocation();

  const is_current_route = extra_match
    ? location.pathname.includes(extra_match) || location.pathname === to
    : index
      ? location.pathname === to
      : location.pathname.startsWith(to);

  const classes = clsx(
    "border-b-3",
    "cursor-pointer",
    "ease",
    "flex",
    "hover:text-almost-white",
    "p-2",
    "select-none",
    "transition-all",
    {
      "hover:border-almost-white": !is_current_route,
      "border-transparent": !is_current_route,
      "text-gray-400": !is_current_route,
      "border-future-purple-400": is_current_route,
      "text-almost-white": is_current_route,
    },
  );
  return (
    <Link {...rest} to={to} className={classes}>
      {children}
    </Link>
  );
};
